import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router";
import SEO from "../SEO";
import { useUser } from "../../lib/contexts/UserContextUtils";
import { handleOAuthLogin } from "../../lib/Utils/Utils";
import { trackCta } from "../../lib/analytics";
import {
  usePluginList,
  useShowcaseGuilds,
} from "../../lib/contexts/PluginListContextUtils";
import { PuzzlePieceIcon } from "@heroicons/react/24/outline";
import featureImage1 from "../../assets/feature-1.webp";
import featureImage2 from "../../assets/feature-2.webp";
import featureImage3 from "../../assets/feature-3.webp";
import featureImage5 from "../../assets/feature-5.webp";
import {
  FiArrowRight,
  FiChevronDown,
  FiCheck,
  FiGitBranch,
  FiShare2,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { Button, Card, DweebMark } from "../ui";
import { dweebPromoUrl } from "../../lib/Utils/dweeb";
import { ExplainerVideo } from "./ExplainerVideo";

const HEADER_HEIGHT = 80;

type Feature = {
  id: number;
  title: string;
  description: string;
  benefits: string[];
  image: string;
};

const SHOWCASE_LIMIT = 8;

/* Integrations beyond this many get a text link under the cards instead, so
   every integration page is linked from the homepage. */
const PLUGIN_CARD_LIMIT = 8;
/* Cards from this index on are hidden on phones (below sm). */
const PLUGIN_CARD_PHONE_LIMIT = 4;

/* Verbatim copy of the static <meta name="description"> in index.html (and
   HOME_DESCRIPTION in scripts/generate-homepage.ts). That tag is
   Helmet-managed (data-rh), so Helmet removes it once it takes over the head
   unless this page emits its own. Keep the copies identical. */
const HOME_DESCRIPTION =
  "Free Discord bot that auto-assigns roles based on YouTube subscriptions, Twitch follows, Steam playtime, Roblox accounts, GitHub contributions, Top.gg votes, and IF-THEN conditions. No coding required.";

/** Where a "start" button sits on the page, sent as cta_placement. */
type StartCtaPlacement = "hero" | "video" | "feature" | "footer";

type StartCta = { placement: StartCtaPlacement; label: string };

/* The same seo_cta_click the static pages send. cta_state separates returning
   users heading to their dashboard from new sign-ups. */
const trackStartCta = (cta: StartCta, loggedIn: boolean) =>
  trackCta({
    link_text: cta.label,
    cta_placement: cta.placement,
    cta_state: loggedIn ? "returning" : "new",
  });

const DISCORD_FLOWER_STAR_PATH =
  "M5.52995 0.431867C6.27995 0.181867 7.2 1.23199 8 1.23199C8.8 1.23199 9.75007 0.231867 10.4701 0.431867C11.19 0.631965 11.38 2.07173 12 2.52171C12.62 2.9717 14.0003 2.70184 14.4603 3.32184C14.9199 3.94191 14.2303 5.16178 14.4603 5.91169C14.6903 6.66159 15.9998 7.21166 16 8.00153C16 8.79146 14.72 9.38146 14.4798 10.0914C14.2398 10.8014 14.9198 12.0919 14.4798 12.6819C14.0397 13.2716 12.6401 13.0321 12.0202 13.482C11.4002 13.932 11.2298 15.3219 10.4798 15.5719C9.72987 15.8216 8.80967 14.7717 8.00973 14.7717C7.2098 14.7719 6.25964 15.7718 5.53971 15.5719C4.81995 15.3716 4.61982 13.9321 4 13.482C3.38 13.032 1.99971 13.3019 1.53971 12.6819C1.07975 12.0619 1.76971 10.8414 1.53971 10.0914C1.30939 9.34159 0 8.79139 0 8.00153C0.000177681 7.21159 1.2802 6.62163 1.52018 5.91169C1.76012 5.20167 1.08021 3.91183 1.52018 3.32184C1.96018 2.73184 3.36999 3.0017 4 2.52171C4.62997 2.04173 4.78003 0.681954 5.52995 0.431867Z";

const DiscordPartnerBadge: React.FC<{ className?: string; title?: string }> = ({
  className,
  title,
}) => (
  <svg
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label={title}
  >
    {title && <title>{title}</title>}
    <path
      fill="#5865F2"
      fillRule="evenodd"
      clipRule="evenodd"
      d={DISCORD_FLOWER_STAR_PATH}
    />
    <g transform="translate(8 8) scale(0.7) translate(-8 -8)">
      <path
        fill="#fff"
        d="M10.5906 6.39993L9.19223 7.29993C8.99246 7.39993 8.89258 7.39993 8.69281 7.29993C8.59293 7.19993 8.39317 7.09993 8.29328 6.99993C7.89375 6.89993 7.5941 6.99993 7.29445 7.19993L6.79504 7.49993L4.29797 9.19993C3.69867 9.49993 2.99949 9.39993 2.69984 8.79993C2.30031 8.29993 2.50008 7.59993 2.99949 7.19993L5.99598 5.19993C6.79504 4.69993 7.79387 4.49993 8.69281 4.69993C9.49188 4.89993 10.0912 5.29993 10.5906 5.89993C10.7904 6.09993 10.6905 6.29993 10.5906 6.39993Z"
      />
      <path
        fill="#fff"
        d="M13.4871 7.79985C13.4871 8.19985 13.2874 8.59985 12.9877 8.79985L9.89135 10.7999C9.29206 11.1999 8.69276 11.3999 7.99358 11.3999C7.69393 11.3999 7.49417 11.3999 7.19452 11.2999C6.39545 11.0999 5.79616 10.6999 5.29674 10.0999C5.19686 9.89985 5.29674 9.69985 5.39663 9.59985L6.79499 8.69985C6.89487 8.59985 7.09463 8.59985 7.19452 8.69985C7.39428 8.79985 7.59405 8.89985 7.69393 8.99985C8.09346 8.99985 8.39311 8.99985 8.69276 8.79985L9.39194 8.39985L11.3896 6.99985L11.6892 6.79985C12.1887 6.49985 12.9877 6.59985 13.2874 7.09985C13.4871 7.39985 13.4871 7.59985 13.4871 7.79985Z"
      />
    </g>
  </svg>
);

const DiscordVerifiedBadge: React.FC<{
  className?: string;
  title?: string;
}> = ({ className, title }) => (
  <svg
    viewBox="0 0 16 16"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label={title}
  >
    {title && <title>{title}</title>}
    <path
      fill="#23A559"
      fillRule="evenodd"
      clipRule="evenodd"
      d={DISCORD_FLOWER_STAR_PATH}
    />
    <g transform="translate(8 8) scale(0.46) translate(-12 -12)">
      <path
        fill="#fff"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.7 7.3a1 1 0 0 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4l3.3 3.29 7.3-7.3a1 1 0 0 1 1.4 0Z"
      />
    </g>
  </svg>
);

type TokenKind =
  | "op" // plain connector words: or / over / 5+ ...
  | "role" // a Discord @role
  | "src" // a platform / signal (Twitch, Steam, VPN ...)
  | "num" // a number, tier or duration
  | "add" // give
  | "sync"; // mirror across servers

type RuleToken = { kind: TokenKind; text: string };

/* Either a plugin icon image (path under /plugin/icons) or, for core features
   that aren't plugins, a react-icon component matching the use case. */
type RuleIcon = string | React.ComponentType<{ className?: string }>;

type DemoRule = {
  tag: string; // broad use-case category (drives the eyebrow)
  name: string; // the real integration / feature name
  icon: RuleIcon;
  condition: RuleToken[];
  action: RuleToken[];
  result: string;
};

/* Plain-language previews of real RoleLogic integrations & features, spanning
   the full breadth of what it solves. Role / platform names stay as-is (they
   read as proper nouns, like a Discord @mention), so no i18n parity risk. */
const DEMO_RULES: DemoRule[] = [
  {
    tag: "conditional role logic",
    name: "Role Conditions",
    icon: FiGitBranch,
    condition: [
      { kind: "role", text: "@Booster" },
      { kind: "op", text: "or" },
      { kind: "role", text: "@Premium" },
    ],
    action: [
      { kind: "add", text: "Give" },
      { kind: "role", text: "@VIP" },
    ],
    result: "Auto-synced for 12,480 members",
  },
  {
    tag: "creator & streaming",
    name: "Twitch Follower Role",
    icon: "/plugin/icons/twitch-follower-role.webp",
    condition: [
      { kind: "src", text: "Twitch" },
      { kind: "num", text: "Tier 3" },
    ],
    action: [
      { kind: "add", text: "Give" },
      { kind: "role", text: "@SubGold" },
    ],
    result: "Updated in real time · 2,140 members",
  },
  {
    tag: "game verification",
    name: "Steam Player Role",
    icon: "/plugin/icons/steam-player-role.webp",
    condition: [
      { kind: "src", text: "Steam" },
      { kind: "op", text: "playtime" },
      { kind: "num", text: "over 100h" },
    ],
    action: [
      { kind: "add", text: "Give" },
      { kind: "role", text: "@Veteran" },
    ],
    result: "Verified 5,640 players",
  },
  {
    tag: "developer activity",
    name: "GitHub Contributor Role",
    icon: "/plugin/icons/github-contributor-role.webp",
    condition: [
      { kind: "src", text: "GitHub" },
      { kind: "op", text: "merged PRs" },
      { kind: "num", text: "5+" },
    ],
    action: [
      { kind: "add", text: "Give" },
      { kind: "role", text: "@Contributor" },
    ],
    result: "Tracking 1,480 contributors",
  },
  {
    tag: "time-limited rewards",
    name: "Top.gg Voter Role",
    icon: "/plugin/icons/topgg-voter-role.webp",
    condition: [
      { kind: "src", text: "Top.gg" },
      { kind: "op", text: "voted" },
    ],
    action: [
      { kind: "add", text: "Give" },
      { kind: "role", text: "@Voter" },
      { kind: "num", text: "for 12h" },
    ],
    result: "Auto-expires · 18,300 grants",
  },
  {
    tag: "anti-alt security",
    name: "Member Origin Role",
    icon: "/plugin/icons/member-origin-role.webp",
    condition: [
      { kind: "src", text: "VPN" },
      { kind: "op", text: "or" },
      { kind: "src", text: "alt account" },
    ],
    action: [
      { kind: "add", text: "Give" },
      { kind: "role", text: "@NeedsReview" },
    ],
    result: "Flagged 212 risky joins",
  },
  {
    tag: "forms & quizzes",
    name: "Form Respondent Role",
    icon: "/plugin/icons/form-respondent-role.webp",
    condition: [
      { kind: "op", text: "score" },
      { kind: "num", text: "80%+" },
    ],
    action: [
      { kind: "add", text: "Give" },
      { kind: "role", text: "@Verified" },
    ],
    result: "Graded 4,021 submissions",
  },
  {
    tag: "time-based roles",
    name: "Birthday Role",
    icon: "/plugin/icons/birthday-role.webp",
    condition: [
      { kind: "src", text: "Birthday" },
      { kind: "op", text: "today" },
    ],
    action: [
      { kind: "add", text: "Give" },
      { kind: "role", text: "@Birthday" },
      { kind: "num", text: "for 24h" },
    ],
    result: "Celebrated 932 members today",
  },
  {
    tag: "cross-server sync",
    name: "Cross-Server Actions",
    icon: FiShare2,
    condition: [
      { kind: "role", text: "@Staff" },
      { kind: "op", text: "in main server" },
    ],
    action: [
      { kind: "sync", text: "Mirror" },
      { kind: "op", text: "to" },
      { kind: "num", text: "6 linked servers" },
    ],
    result: "Synced across your network",
  },
];

const TOKEN_CLASSES: Record<TokenKind, string> = {
  op: "text-zinc-400",
  role:
    "rounded-md bg-indigo-500/15 px-2 py-0.5 font-medium text-indigo-200 ring-1 ring-inset ring-indigo-400/25",
  src:
    "rounded-md bg-rose-500/15 px-2 py-0.5 font-medium text-rose-200 ring-1 ring-inset ring-rose-400/25",
  num:
    "rounded-md bg-amber-500/10 px-2 py-0.5 font-medium text-amber-300 ring-1 ring-inset ring-amber-400/20",
  add: "font-semibold text-emerald-400",
  sync: "font-semibold text-indigo-300",
};

const RuleTokens: React.FC<{ tokens: RuleToken[] }> = ({ tokens }) => (
  <>
    {tokens.map((tok, i) => (
      <span key={i} className={TOKEN_CLASSES[tok.kind]}>
        {tok.text}
      </span>
    ))}
  </>
);

/* One line of the rule. The stagger delay only applies on the way IN, so the
   logic appears to "build" itself (when → if → then → result); on the way
   out everything fades together. */
const AnimatedLine: React.FC<{
  visible: boolean;
  delay: number;
  className?: string;
  children: React.ReactNode;
}> = ({ visible, delay, className, children }) => (
  <div
    style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
    className={`transition-all duration-500 ${
      visible
        ? "opacity-100 translate-y-0 blur-0"
        : "opacity-0 translate-y-1 blur-[1px]"
    } ${className ?? ""}`}
  >
    {children}
  </div>
);

const RuleEditorCard: React.FC<{ rule: DemoRule; isVisible: boolean }> = ({
  rule,
  isVisible,
}) => {
  const IconComponent = typeof rule.icon === "string" ? null : rule.icon;
  return (
    <div className="relative w-full max-w-md">
      {/* anchored glow — replaces the generic floating blob */}
      <div
        aria-hidden="true"
        className="absolute -inset-6 -z-10 rounded-[2rem] bg-indigo-600/20 blur-3xl"
      />
      <Card
        surface={2}
        padding="none"
        className="overflow-hidden border-border-default shadow-2xl shadow-indigo-950/40"
      >
        {/* header — the real integration / feature, with its own icon */}
        <div className="flex items-center gap-3 border-b border-border-subtle px-5 py-4">
          {IconComponent ? (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-300 ring-1 ring-inset ring-indigo-400/25">
              <IconComponent className="h-5 w-5" />
            </div>
          ) : (
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded-xl bg-surface-raised ring-1 ring-inset ring-border-subtle">
              <img
                src={rule.icon as string}
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">
              {rule.name}
            </p>
            <p className="truncate text-xs capitalize text-zinc-500">
              {rule.tag}
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-400 ring-1 ring-inset ring-emerald-400/20">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 motion-safe:animate-pulse" />
            Active
          </span>
        </div>

        {/* simple if → then — builds itself step by step */}
        <div className="flex gap-4 p-5">
          <div className="rule-rail w-px shrink-0 rounded-full bg-border-default" />
          <div className="flex-1 space-y-2.5 text-sm">
            <AnimatedLine
              visible={isVisible}
              delay={0}
              className="flex items-start gap-3"
            >
              <span className="inline-flex h-7 w-14 shrink-0 items-center justify-center rounded-md bg-white/[0.06] text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                If
              </span>
              <span className="flex min-h-7 flex-1 flex-wrap items-center gap-x-2 gap-y-1.5">
                <RuleTokens tokens={rule.condition} />
              </span>
            </AnimatedLine>

            <AnimatedLine
              visible={isVisible}
              delay={150}
              className="flex items-center gap-3 text-indigo-400/60"
            >
              <span className="h-3 w-14 shrink-0" />
              <FiChevronDown className="shrink-0 motion-safe:animate-pulse" />
              <span className="h-px flex-1 bg-gradient-to-r from-indigo-500/40 to-transparent" />
            </AnimatedLine>

            <AnimatedLine
              visible={isVisible}
              delay={300}
              className="flex items-start gap-3"
            >
              <span className="inline-flex h-7 w-14 shrink-0 items-center justify-center rounded-md bg-indigo-500/15 text-[10px] font-bold uppercase tracking-wider text-indigo-300 ring-1 ring-inset ring-indigo-400/20">
                Then
              </span>
              <span className="flex min-h-7 flex-1 flex-wrap items-center gap-x-2 gap-y-1.5">
                <RuleTokens tokens={rule.action} />
                <span className="rule-caret ml-0.5 inline-block h-4 w-[2px] translate-y-0.5 bg-indigo-400" />
              </span>
            </AnimatedLine>
          </div>
        </div>

        {/* footer — the friendly outcome */}
        <div className="flex items-center gap-2 border-t border-border-subtle bg-black/20 px-5 py-3">
          <FiCheck className="shrink-0 text-emerald-400" />
          <AnimatedLine
            visible={isVisible}
            delay={450}
            className="text-xs text-zinc-400"
          >
            {rule.result}
          </AnimatedLine>
        </div>
      </Card>
    </div>
  );
};

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { user, initialized } = useUser();
  const navigate = useNavigate();
  const [hoveredFeatureId, setHoveredFeatureId] = useState<number | null>(null);
  const [loopIndex, setLoopIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [clickPending, setClickPending] = useState(false);
  // The start button behind a click made before the session check finished;
  // the deferred branch reports it once it knows where the click leads.
  const pendingCtaRef = useRef<StartCta | null>(null);

  const { plugins } = usePluginList();
  const { featuredPlugins, morePlugins } = useMemo(() => {
    const featured = plugins.filter((p) => p.featured);
    const rest = plugins.filter((p) => !p.featured);
    const ordered = [...featured, ...rest];
    return {
      featuredPlugins: ordered.slice(0, PLUGIN_CARD_LIMIT),
      morePlugins: ordered.slice(PLUGIN_CARD_LIMIT),
    };
  }, [plugins]);
  const phoneOnlyPlugins = featuredPlugins.slice(PLUGIN_CARD_PHONE_LIMIT);

  const { guilds: showcaseGuildsRaw } = useShowcaseGuilds();
  const showcaseGuilds = useMemo(
    () =>
      showcaseGuildsRaw
        .filter((g) => g.icon_url)
        .sort((a, b) => {
          if (a.is_verified !== b.is_verified) {
            return a.is_verified ? -1 : 1;
          }
          if (a.is_partnered !== b.is_partnered) {
            return a.is_partnered ? -1 : 1;
          }
          return b.member_count - a.member_count;
        })
        .slice(0, SHOWCASE_LIMIT),
    [showcaseGuildsRaw],
  );

  const FEATURES: Feature[] = [
    {
      id: 1,
      title: t("HomePage.feature1Title"),
      description: t("HomePage.feature1Description"),
      benefits: [
        t("HomePage.feature1Benefit1"),
        t("HomePage.feature1Benefit2"),
        t("HomePage.feature1Benefit3"),
      ],
      image: featureImage1,
    },
    {
      id: 2,
      title: t("HomePage.feature2Title"),
      description: t("HomePage.feature2Description"),
      benefits: [
        t("HomePage.feature2Benefit1"),
        t("HomePage.feature2Benefit2"),
        t("HomePage.feature2Benefit3"),
      ],
      image: featureImage2,
    },
    {
      id: 3,
      title: t("HomePage.feature3Title"),
      description: t("HomePage.feature3Description"),
      benefits: [
        t("HomePage.feature3Benefit1"),
        t("HomePage.feature3Benefit2"),
        t("HomePage.feature3Benefit3"),
      ],
      image: featureImage3,
    },
    {
      id: 5,
      title: t("HomePage.feature5Title"),
      description: t("HomePage.feature5Description"),
      benefits: [
        t("HomePage.feature5Benefit1"),
        t("HomePage.feature5Benefit2"),
        t("HomePage.feature5Benefit3"),
      ],
      image: featureImage5,
    },
  ];

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduceMotion) return;

    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setLoopIndex((prev) => (prev + 1) % DEMO_RULES.length);
        setIsVisible(true);
      }, 300);
    }, 7000);

    return () => clearInterval(interval);
  }, []);

  const activeRule = DEMO_RULES[loopIndex % DEMO_RULES.length];

  useEffect(() => {
    if (!clickPending || !initialized) return;
    setClickPending(false);
    if (pendingCtaRef.current) {
      trackStartCta(pendingCtaRef.current, !!user);
      pendingCtaRef.current = null;
    }
    if (user) {
      navigate("/dashboard");
    } else {
      handleOAuthLogin();
    }
  }, [clickPending, initialized, user, navigate]);

  // The CTA event fires only right before leaving the page, so a click made
  // before init is counted once (by the deferred branch above), not twice.
  const handleStartNowClick = useCallback(
    (placement: StartCtaPlacement, label: string) => {
      if (!initialized) {
        pendingCtaRef.current = { placement, label };
        setClickPending(true);
        return;
      }
      trackStartCta({ placement, label }, !!user);
      if (user) {
        navigate("/dashboard");
      } else {
        handleOAuthLogin();
      }
    },
    [user, initialized, navigate],
  );

  const handleSeeFeaturesClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const featureSection = document.getElementById("features");
      if (!featureSection) return;

      const yOffset = -HEADER_HEIGHT;
      const y =
        featureSection.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    },
    [],
  );

  return (
    <div className="w-full bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      <SEO path="/" description={HOME_DESCRIPTION} />

      {/* Hero Section */}
      <section className="relative flex items-center min-h-screen overflow-hidden px-6 pb-16 pt-24 sm:px-12 lg:pb-20 lg:pt-28">
        {/* Layered backdrop — depth without generic floating blobs */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          {/* dot grid */}
          <div className="absolute inset-0 [background-image:radial-gradient(circle,rgba(129,140,248,0.13)_1px,transparent_1px)] [background-size:26px_26px] [mask-image:radial-gradient(ellipse_75%_60%_at_50%_35%,black,transparent)]" />
          {/* anchored focal glow behind the rule card */}
          <div className="absolute right-[-12%] top-1/2 h-[640px] w-[640px] -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(99,102,241,0.20),transparent_70%)]" />
          {/* secondary cool glow, lower-left, for colour balance */}
          <div className="absolute -bottom-40 -left-32 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(139,92,246,0.12),transparent_70%)]" />
          {/* slow drifting sheen — oversized so its own edges never show */}
          <div className="hero-sheen absolute [inset:-40%]" />
          {/* perspective engine-floor grid for depth at the bottom */}
          <div className="absolute inset-x-0 bottom-0 h-72 origin-bottom [background-image:linear-gradient(rgba(129,140,248,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(129,140,248,0.12)_1px,transparent_1px)] [background-size:48px_48px] [mask-image:linear-gradient(to_top,black,transparent)] [transform:perspective(440px)_rotateX(60deg)]" />
          {/* fade into the page gradient */}
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-gray-950" />
        </div>

        <div className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left: copy + CTAs */}
          <div className="text-center lg:text-left">
            <h1 className="text-balance text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
              {/* Brand lockup. This is the only place the product name appears
                  in the homepage H1, and it is deliberately the first text
                  node in it: for the navigational query "rolelogic" the H1 is
                  the strongest on-page signal that this URL is the brand's
                  home. Without it the H1 read "Discord Role Automation
                  Extended" — no brand token at all — and the site's own docs
                  pages (whose titles do carry "RoleLogic") outranked it.
                  Not translated: a brand name is invariant across locales.
                  The {" "} after the span is load-bearing: without it the H1's
                  textContent concatenates to "RoleLogicDiscord", which naive
                  extractors read as one token. It collapses visually. */}
              <span className="mb-3 block text-sm font-semibold uppercase tracking-[0.25em] text-indigo-300 sm:mb-4 sm:text-base">
                RoleLogic
              </span>{" "}
              {t("HomePage.headerTitle")}
              <br className="hidden md:block" />
              <span className="text-indigo-300">
                {t("HomePage.headerTitleBreak")}
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-pretty text-lg text-zinc-300 sm:mt-7 sm:text-xl lg:mx-0">
              {t("HomePage.headerSubtitle")}
            </p>

            <div className="mt-8 flex flex-col items-center gap-4 sm:mt-10 sm:flex-row sm:justify-center lg:justify-start">
              <Button
                size="lg"
                loading={clickPending}
                onClick={() =>
                  handleStartNowClick("hero", t("HomePage.startNowButton"))
                }
                iconEnd={<FiArrowRight className="text-lg" />}
                className="hover:-translate-y-0.5 active:scale-95 shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40"
              >
                {t("HomePage.startNowButton")}
              </Button>
              <Button
                asChild
                variant="ghost"
                size="lg"
                className="hover:-translate-y-0.5 active:scale-95 shadow-lg"
              >
                <a href="#features" onClick={handleSeeFeaturesClick}>
                  {t("HomePage.exploreFeaturesButton")}
                  <FiChevronDown className="text-lg" />
                </a>
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 font-mono text-xs text-zinc-500 sm:mt-10 lg:justify-start">
              <span className="text-zinc-600">works with</span>
              {["Twitch", "YouTube", "Steam", "GitHub"].map((name, i) => (
                <React.Fragment key={name}>
                  {i > 0 && <span className="text-zinc-700">·</span>}
                  <span className="text-zinc-400">{name}</span>
                </React.Fragment>
              ))}
              <span className="text-zinc-700">·</span>
              <span className="text-indigo-400/80">+ more</span>
            </div>
          </div>

          {/* Right: live rule-engine preview */}
          <div className="flex justify-center lg:justify-end">
            <RuleEditorCard rule={activeRule} isVisible={isVisible} />
          </div>
        </div>

        {/* scroll cue — signals there's more content below the fold */}
        <a
          href="#features"
          onClick={handleSeeFeaturesClick}
          aria-label={t("HomePage.exploreFeaturesButton")}
          className="group absolute bottom-3 left-1/2 z-10 flex h-12 w-20 -translate-x-1/2 items-center justify-center"
        >
          <span className="scroll-line block h-1 w-10 rounded-full bg-white/25 group-hover:bg-indigo-300/80" />
        </a>
      </section>

      {/* Trusted By Section */}
      {showcaseGuilds.length > 0 && (
        <section className="relative py-16 px-4 max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="relative mb-5 inline-flex items-center gap-2.5 px-4 py-2 font-mono text-xs uppercase tracking-[0.22em] text-zinc-300">
              {/* viewfinder corner brackets — a bespoke HUD frame instead of a
                  stock gradient pill */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-0 top-0 h-2.5 w-2.5 border-l border-t border-indigo-400/50"
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-0 top-0 h-2.5 w-2.5 border-r border-t border-indigo-400/50"
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 left-0 h-2.5 w-2.5 border-b border-l border-indigo-400/50"
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 right-0 h-2.5 w-2.5 border-b border-r border-indigo-400/50"
              />
              {/* live "online" signal */}
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full motion-safe:animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
              </span>
              <span className="-mr-[0.22em]">{t("HomePage.trustedBadge")}</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">
              {t("HomePage.trustedTitle")}
            </h2>
            <p className="text-lg text-zinc-300 max-w-2xl mx-auto">
              {t("HomePage.trustedSubtitle")}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {showcaseGuilds.map((guild, idx) => {
              const content = (
                <>
                  <div className="relative flex-shrink-0 w-12 h-12">
                    <div className="w-full h-full rounded-full bg-gray-700 overflow-hidden">
                      {guild.icon_url && (
                        <img
                          src={guild.icon_url}
                          alt={guild.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                    </div>
                    {guild.is_partnered && (
                      <DiscordPartnerBadge
                        title={t("HomePage.trustedPartnerBadge")}
                        className="absolute -bottom-1 -right-1 w-5 h-5 drop-shadow-[0_0_2px_rgba(17,24,39,1)]"
                      />
                    )}
                    {guild.is_verified && !guild.is_partnered && (
                      <DiscordVerifiedBadge
                        title={t("HomePage.trustedVerifiedBadge")}
                        className="absolute -bottom-1 -right-1 w-5 h-5 drop-shadow-[0_0_2px_rgba(17,24,39,1)]"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-white truncate">
                      {guild.name}
                    </h3>
                    <p className="text-xs text-gray-300">
                      {t("HomePage.trustedMemberCount", {
                        count: guild.member_count,
                        formatted: guild.member_count.toLocaleString(),
                      })}
                    </p>
                  </div>
                </>
              );
              const visibility = idx >= 4 ? "hidden sm:flex" : "flex";
              const className = `${visibility} items-center gap-3 hover:border-indigo-500/40 transition-colors`;
              return guild.vanity_url_code ? (
                <Card
                  key={guild.id}
                  as="a"
                  surface={2}
                  padding="sm"
                  href={`https://discord.gg/${guild.vanity_url_code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={className}
                >
                  {content}
                </Card>
              ) : (
                <Card
                  key={guild.id}
                  surface={2}
                  padding="sm"
                  className={className}
                >
                  {content}
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* The explainer tour, after the servers that already use it. */}
      <ExplainerVideo
        startLabel={t("HomePage.videoStart")}
        loading={clickPending}
        onStart={() => handleStartNowClick("video", t("HomePage.videoStart"))}
      />

      {/* Features Section */}
      <section id="features" className="py-16 md:py-20 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="mb-4 text-3xl md:text-4xl font-bold">
            {t("HomePage.featuresTitle")}
          </h2>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            {t("HomePage.featuresSubtitle")}
          </p>
        </div>

        <div className="space-y-28 md:space-y-32">
          {FEATURES.map((feature, idx) => (
            <div
              key={feature.id}
              className={`flex flex-col gap-10 md:gap-12 items-center md:flex-row ${
                idx % 2 ? "md:flex-row-reverse" : "md:flex-row"
              }`}
            >
              {/* Image section */}
              <div
                onMouseEnter={() => setHoveredFeatureId(feature.id)}
                onMouseLeave={() => setHoveredFeatureId(null)}
                className={`flex-[1.3] transition-transform duration-500 w-full ${
                  hoveredFeatureId === feature.id ? "scale-110" : "scale-100"
                }`}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-auto object-contain"
                  />
                  <div className="absolute inset-0" />
                </div>
              </div>

              {/* Text section */}
              <div className="flex-1 w-full">
                <h3 className="mb-4 text-2xl md:text-3xl font-bold">
                  {feature.title}
                </h3>
                <p className="mb-6 text-zinc-300 max-w-lg">
                  {feature.description}
                </p>
                <div className="mb-8 space-y-3">
                  {feature.benefits.map((b, i) => (
                    <div key={i} className="flex items-start">
                      <div className="mr-3 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500/10 mt-0.5">
                        <FiCheck className="text-sm text-indigo-400" />
                      </div>
                      <span className="text-zinc-300">{b}</span>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() =>
                    handleStartNowClick(
                      "feature",
                      t("HomePage.startUsingNowButton"),
                    )
                  }
                  loading={clickPending}
                  className="bg-indigo-600 hover:bg-indigo-700 from-indigo-600 to-indigo-600 hover:from-indigo-700 hover:to-indigo-700 shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40"
                >
                  {t("HomePage.startUsingNowButton")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Plugin Teaser Section */}
      {featuredPlugins.length > 0 && (
        <section className="relative py-16 px-4 max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">
              {t("HomePage.pluginsSectionTitle")}
            </h2>
            <p className="text-lg text-zinc-300 max-w-2xl mx-auto">
              {t("HomePage.pluginsSectionSubtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {featuredPlugins.map((plugin, idx) => (
              <Card
                key={plugin.id}
                as="a"
                surface={2}
                padding="md"
                href={`/integrations/${plugin.id}`}
                className={`${idx >= PLUGIN_CARD_PHONE_LIMIT ? "hidden sm:block" : ""} hover:border-indigo-500/40 transition-colors`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center overflow-hidden">
                    {plugin.icon_url ? (
                      <img
                        src={plugin.icon_url}
                        alt={plugin.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <PuzzlePieceIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-white">
                    {plugin.name}
                  </h3>
                </div>
                <p className="text-xs text-gray-300 line-clamp-2">
                  {plugin.short_description}
                </p>
              </Card>
            ))}
          </div>
          {/* Every integration without a visible card, as plain links, so each
              integration page is one visible hop from the homepage at every
              width. Cards hidden on phones are listed here on phones only. */}
          {(morePlugins.length > 0 || phoneOnlyPlugins.length > 0) && (
            <div
              className={`mx-auto mb-10 max-w-4xl text-center${morePlugins.length === 0 ? " sm:hidden" : ""}`}
            >
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-zinc-400">
                {t("Home.moreIntegrations", "More integrations")}
              </h3>
              <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm">
                {phoneOnlyPlugins.map((plugin) => (
                  <li key={plugin.id} className="sm:hidden">
                    <a
                      href={`/integrations/${plugin.id}`}
                      className="text-zinc-300 underline decoration-zinc-600 underline-offset-4 transition-colors hover:text-white hover:decoration-indigo-400"
                    >
                      {plugin.name}
                    </a>
                  </li>
                ))}
                {morePlugins.map((plugin) => (
                  <li key={plugin.id}>
                    <a
                      href={`/integrations/${plugin.id}`}
                      className="text-zinc-300 underline decoration-zinc-600 underline-offset-4 transition-colors hover:text-white hover:decoration-indigo-400"
                    >
                      {plugin.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="text-center">
            <Button
              asChild
              iconEnd={<FiArrowRight />}
              className="bg-indigo-600 hover:bg-indigo-700 from-indigo-600 to-indigo-600 hover:from-indigo-700 hover:to-indigo-700 shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40"
            >
              <a href="/integrations/">{t("HomePage.viewAllPlugins")}</a>
            </Button>
          </div>
        </section>
      )}

      {/* DWEEB cross-promo — companion webhook-message builder */}
      <section className="relative py-16 px-4 max-w-6xl mx-auto">
        <Card
          surface={2}
          padding="lg"
          className="hover:border-[#5865F2]/40 transition-colors"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <DweebMark className="w-16 h-16 flex-shrink-0 rounded-2xl shadow-lg shadow-[#5865F2]/20" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#8891f2] mb-1">
                {t("HomePage.dweebEyebrow")}
              </p>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                {t("HomePage.dweebTitle")}
              </h2>
              <p className="text-sm sm:text-base text-zinc-300 max-w-2xl">
                {t("HomePage.dweebDescription")}
              </p>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                {[
                  t("HomePage.dweebBenefit1"),
                  t("HomePage.dweebBenefit2"),
                  t("HomePage.dweebBenefit3"),
                  t("HomePage.dweebBenefit4"),
                ].map((b, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-2 text-sm text-zinc-300"
                  >
                    <FiCheck className="text-[#8891f2] flex-shrink-0" />
                    {b}
                  </span>
                ))}
              </div>
            </div>
            <Button
              asChild
              iconEnd={<FiArrowRight />}
              className="flex-shrink-0 bg-[#5865F2] hover:bg-[#4752c4] from-[#5865F2] to-[#5865F2] hover:from-[#4752c4] hover:to-[#4752c4] shadow-lg shadow-[#5865F2]/20 hover:shadow-[#5865F2]/40"
            >
              <a
                href={dweebPromoUrl("homepage")}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("HomePage.dweebButton")}
              </a>
            </Button>
          </div>
        </Card>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 px-4 max-w-4xl mx-auto text-center mb-10">
        <div className="inset-0 -z-10 rotate-[-1deg] rounded-3xl bg-gradient-to-r from-indigo-500/10 to-purple-500/10 blur-2xl" />
        <div className="p-8 sm:p-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700/50">
          <h2 className="mb-6 text-2xl sm:text-4xl font-bold">
            {t("HomePage.ctaTitle")}
          </h2>
          <p className="mb-8 text-lg text-zinc-300">
            {t("HomePage.ctaSubtitle")}
          </p>
          <Button
            size="lg"
            onClick={() =>
              handleStartNowClick("footer", t("HomePage.ctaStartNowButton"))
            }
            loading={clickPending}
            className="mx-auto w-fit shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 hover:-translate-y-0.5"
          >
            {t("HomePage.ctaStartNowButton")}
          </Button>
          <p className="mt-4 text-sm text-zinc-500">
            {t("HomePage.ctaDisclaimer")}
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
